/**
 * Arrow Evaluator - Race-focused minimax
 *
 * Philosophy: HYPER focused on king RACE to the goal row
 * - King forward progress is rewarded exponentially
 * - Enemy king progress is heavily penalized
 * - King safety prevents suicide runs
 * - Material is minimal — just avoid free blunders
 * - Support pieces advance to clear path and create threats
 * - Free captures taken when low-risk
 */

import { GameState, IPiece, PieceType, Position, State } from "../../types";
import { EvaluationBreakdown } from "./SentinelEvaluator";
import { isRaceVictory, RACE_VICTORY_BONUS } from "./MinimaxUtils";

export class ArrowEvaluator {
  static lastBreakdown: EvaluationBreakdown | null = null;

  /**
   * Main evaluation — positive = good for AI, negative = bad
   */
  static evaluate(
    gameState: GameState,
    isMaximizing: boolean,
    debug: boolean = false
  ): number {
    const terminalScore = this.checkTerminalState(gameState, isMaximizing);
    if (terminalScore !== null) return terminalScore;

    const aiIsLight = gameState.isLightTurn === isMaximizing;
    let score = 0;

    // 1. KING RACE DISTANCE — the core of Arrow (exponential forward bonus)
    score += this.evaluateKingRace(gameState, aiIsLight) * 5.0;

    // 2. ENEMY KING PROGRESS — penalize their king advancing
    score += this.evaluateEnemyKingProgress(gameState, aiIsLight) * 3.0;

    // 3. KING SAFETY — don't run into death
    score += this.evaluateKingSafety(gameState, aiIsLight) * 2.5;

    // 4. MATERIAL — minimal, just don't blunder pieces
    score += this.evaluateMaterial(gameState, aiIsLight) * 0.3;

    // 5. SUPPORT POSITIONING — advance team to clear path
    score += this.evaluateSupportPositioning(gameState, aiIsLight) * 1.5;

    // 6. FREE CAPTURES — take undefended pieces when available
    score += this.evaluateFreeCaptures(gameState, aiIsLight) * 1.0;

    // 7. STATUS EFFECTS — light weight
    score += this.evaluateStatusEffects(gameState, aiIsLight) * 0.5;

    if (debug) {
      this.buildBreakdown(gameState, aiIsLight);
    }

    return score;
  }

  private static checkTerminalState(
    gameState: GameState,
    isMaximizing: boolean
  ): number | null {
    if (gameState.state === State.PostGame) {
      const aiIsLight = gameState.isLightTurn === isMaximizing;
      const winScore = 100000 + (isRaceVictory(gameState) ? RACE_VICTORY_BONUS : 0);
      if (gameState.lightWon === true) {
        return aiIsLight ? winScore : -winScore;
      }
      if (gameState.lightWon === false) {
        return aiIsLight ? -winScore : winScore;
      }
    }
    if (gameState.state === State.Draw || gameState.state === State.Terminated) {
      return 0;
    }
    return null;
  }

  /**
   * CORE: King race distance with exponential forward bonus.
   * The further our king advances, the more each additional row is worth.
   */
  private static evaluateKingRace(
    gameState: GameState,
    aiIsLight: boolean
  ): number {
    let score = 0;

    for (let row = 0; row < 8; row++) {
      for (let col = 0; col < 5; col++) {
        const pieces = gameState.board[row][col].Pieces;
        for (const piece of pieces) {
          if (!piece.isKing || piece.isLight !== aiIsLight) continue;

          // How far has our king advanced?
          const distFromStart = piece.isLight ? row : 7 - row;

          // Exponential race scoring
          if (distFromStart <= 1) {
            score += distFromStart * 2;       // Starting zone: +0, +2
          } else if (distFromStart <= 3) {
            score += 2 + (distFromStart - 1) * 5;  // Midfield: +7, +12
          } else if (distFromStart <= 5) {
            score += 12 + (distFromStart - 3) * 12; // Enemy territory: +24, +36
          } else if (distFromStart === 6) {
            score += 60;                       // One step from victory!
          }
          // distFromStart === 7 = terminal (handled by checkTerminalState)
        }
      }
    }

    return score;
  }

  /**
   * Penalize enemy king progress — mirror of our race scoring.
   * We want to be aware of the enemy racing too.
   */
  private static evaluateEnemyKingProgress(
    gameState: GameState,
    aiIsLight: boolean
  ): number {
    let score = 0;

    for (let row = 0; row < 8; row++) {
      for (let col = 0; col < 5; col++) {
        const pieces = gameState.board[row][col].Pieces;
        for (const piece of pieces) {
          if (!piece.isKing || piece.isLight === aiIsLight) continue;

          // How far has enemy king advanced?
          const distFromStart = piece.isLight ? row : 7 - row;

          // Penalize enemy progress (negative score = bad for us)
          if (distFromStart <= 1) {
            score -= distFromStart * 2;
          } else if (distFromStart <= 3) {
            score -= 2 + (distFromStart - 1) * 5;
          } else if (distFromStart <= 5) {
            score -= 12 + (distFromStart - 3) * 14; // Slightly heavier penalty
          } else if (distFromStart === 6) {
            score -= 70; // Enemy one step from victory = urgent!
          }
        }
      }
    }

    return score;
  }

  /**
   * King safety — penalize positions where our king is exposed to enemy attacks.
   * Count nearby enemy pieces that could threaten the king's current position.
   */
  private static evaluateKingSafety(
    gameState: GameState,
    aiIsLight: boolean
  ): number {
    let score = 0;

    // Find our king
    let kingRow = -1, kingCol = -1;
    for (let row = 0; row < 8; row++) {
      for (let col = 0; col < 5; col++) {
        const pieces = gameState.board[row][col].Pieces;
        for (const piece of pieces) {
          if (piece.isKing && piece.isLight === aiIsLight) {
            kingRow = row;
            kingCol = col;
          }
        }
      }
    }

    if (kingRow === -1) return -500; // No king = catastrophic

    // Check for nearby enemy pieces that could threaten king
    for (let row = 0; row < 8; row++) {
      for (let col = 0; col < 5; col++) {
        const pieces = gameState.board[row][col].Pieces;
        for (const piece of pieces) {
          if (piece.isLight === aiIsLight) continue; // Skip our pieces
          if (piece.isEntangled) continue; // Entangled can't attack

          const dist = Math.abs(row - kingRow) + Math.abs(col - kingCol);

          // Pieces within attack range are dangerous
          const attackRange = this.getAttackRange(piece.type);
          if (dist <= attackRange) {
            // Closer = more dangerous
            const threatPenalty = (attackRange - dist + 1) * 8;
            score -= threatPenalty;

            // Bear adjacent to king is especially lethal (Savage)
            if (piece.type === PieceType.Bear && dist <= 1) {
              score -= 30;
            }
          }
        }
      }
    }

    // Bonus for king being on edge columns (fewer attack angles)
    if (kingCol === 0 || kingCol === 4) {
      score += 3;
    }

    return score;
  }

  /**
   * Minimal material evaluation — just enough to avoid free blunders
   */
  private static evaluateMaterial(
    gameState: GameState,
    aiIsLight: boolean
  ): number {
    let score = 0;

    for (let row = 0; row < 8; row++) {
      for (let col = 0; col < 5; col++) {
        const pieces = gameState.board[row][col].Pieces;
        for (const piece of pieces) {
          const multiplier = piece.isLight === aiIsLight ? 1 : -1;
          let pieceValue = 10;
          if (piece.isKing) {
            pieceValue += 1000;
          }
          // DOOM: Piece stacked with own Virus is effectively dead
          if (piece.type !== PieceType.Virus && pieces.length > 1) {
            const friendlyVirus = pieces.find(p =>
              p.type === PieceType.Virus && p.isLight === piece.isLight && p !== piece
            );
            if (friendlyVirus) {
              pieceValue = -(pieceValue + 5);
            }
          }
          score += pieceValue * multiplier;
        }
      }
    }

    return score;
  }

  /**
   * Support positioning — reward non-king pieces that advance.
   * Pieces ahead of the king can clear the path; pieces nearby can protect.
   */
  private static evaluateSupportPositioning(
    gameState: GameState,
    aiIsLight: boolean
  ): number {
    let score = 0;

    // Find our king position
    let kingRow = -1;
    for (let row = 0; row < 8; row++) {
      for (let col = 0; col < 5; col++) {
        const pieces = gameState.board[row][col].Pieces;
        for (const piece of pieces) {
          if (piece.isKing && piece.isLight === aiIsLight) {
            kingRow = row;
          }
        }
      }
    }

    for (let row = 0; row < 8; row++) {
      for (let col = 0; col < 5; col++) {
        const pieces = gameState.board[row][col].Pieces;
        for (const piece of pieces) {
          if (piece.isLight !== aiIsLight) continue;
          if (piece.isKing) continue; // King handled separately
          if (piece.type === PieceType.Virus) continue; // Virus doesn't advance

          const distFromStart = piece.isLight ? row : 7 - row;

          // Reward forward positioning (linear, not exponential)
          score += distFromStart * 2;

          // Bonus for pieces ahead of or near the king (scouting/clearing)
          if (kingRow >= 0) {
            const aheadOfKing = piece.isLight
              ? row > kingRow  // Light: higher row = ahead
              : row < kingRow; // Dark: lower row = ahead
            if (aheadOfKing) {
              score += 4; // Scouting bonus
            }
          }
        }
      }
    }

    return score;
  }

  /**
   * Free captures — reward positions where we can capture undefended enemy pieces.
   * Simple heuristic: count enemy pieces adjacent to our pieces with no allies nearby.
   */
  private static evaluateFreeCaptures(
    gameState: GameState,
    aiIsLight: boolean
  ): number {
    let score = 0;

    for (let row = 0; row < 8; row++) {
      for (let col = 0; col < 5; col++) {
        const pieces = gameState.board[row][col].Pieces;
        for (const piece of pieces) {
          if (piece.isLight === aiIsLight) continue; // Only enemy pieces
          if (piece.isKing) continue; // King capture handled by terminal state

          // Count our attackers and their defenders nearby
          let ourAttackers = 0;
          let theirDefenders = 0;

          for (let dr = -1; dr <= 1; dr++) {
            for (let dc = -1; dc <= 1; dc++) {
              if (dr === 0 && dc === 0) continue;
              const nr = row + dr;
              const nc = col + dc;
              if (nr < 0 || nr >= 8 || nc < 0 || nc >= 5) continue;

              const neighbors = gameState.board[nr][nc].Pieces;
              for (const neighbor of neighbors) {
                if (neighbor.isEntangled) continue;
                if (neighbor.isLight === aiIsLight) {
                  ourAttackers++;
                } else {
                  theirDefenders++;
                }
              }
            }
          }

          // Undefended enemy piece we can reach = free capture opportunity
          if (ourAttackers > 0 && theirDefenders === 0) {
            score += 12;
          } else if (ourAttackers > theirDefenders) {
            score += 5; // We have more attackers than they have defenders
          }
        }
      }
    }

    return score;
  }

  /**
   * Status effects — light weight scoring
   */
  private static evaluateStatusEffects(
    gameState: GameState,
    aiIsLight: boolean
  ): number {
    let score = 0;

    for (let row = 0; row < 8; row++) {
      for (let col = 0; col < 5; col++) {
        const pieces = gameState.board[row][col].Pieces;
        for (const piece of pieces) {
          const isOurs = piece.isLight === aiIsLight;

          if (!isOurs) {
            if (piece.isEntangled) score += 15;
            if (piece.poisonedStatus?.isPoisoned) score += 10;
            if (piece.isFainted) score += 12;
          } else {
            if (piece.isEntangled) score -= 10;
            if (piece.poisonedStatus?.isPoisoned) score -= 8;
          }
        }
      }
    }

    return score;
  }

  /**
   * Get approximate attack range for a piece type (Manhattan distance)
   */
  private static getAttackRange(pieceType: PieceType): number {
    switch (pieceType) {
      case PieceType.Snake: return 2;
      case PieceType.Crane: return 2;
      case PieceType.Frog: return 2;
      case PieceType.Rhino: return 3;
      case PieceType.Penguin: return 7;
      case PieceType.Virus: return 2;
      default: return 1;
    }
  }

  /**
   * Quick evaluation for move ordering
   */
  static quickEvaluate(gameState: GameState, isMaximizing: boolean): number {
    const aiIsLight = gameState.isLightTurn === isMaximizing;
    let score = 0;

    for (let row = 0; row < 8; row++) {
      for (let col = 0; col < 5; col++) {
        const pieces = gameState.board[row][col].Pieces;
        for (const piece of pieces) {
          const multiplier = piece.isLight === aiIsLight ? 1 : -1;
          let pieceValue = 10;
          if (piece.isKing) {
            pieceValue += 1000;
            // Quick race bonus for king
            const dist = piece.isLight ? row : 7 - row;
            pieceValue += dist * 8;
          }
          // DOOM: Piece stacked with own Virus is effectively dead
          if (piece.type !== PieceType.Virus && pieces.length > 1) {
            const friendlyVirus = pieces.find(p =>
              p.type === PieceType.Virus && p.isLight === piece.isLight && p !== piece
            );
            if (friendlyVirus) {
              pieceValue = -(pieceValue + 5);
            }
          }
          score += pieceValue * multiplier;
        }
      }
    }

    return score;
  }

  /**
   * Absolute board value from Light's perspective (for PV display)
   */
  static getAbsoluteBoardValue(gameState: GameState): number {
    if (gameState.state === State.PostGame) {
      const winScore = 100000 + (isRaceVictory(gameState) ? RACE_VICTORY_BONUS : 0);
      if (gameState.lightWon === true) return winScore;
      if (gameState.lightWon === false) return -winScore;
    }
    if (gameState.state === State.Draw || gameState.state === State.Terminated) {
      return 0;
    }

    let lightTotal = 0;
    let darkTotal = 0;

    for (let row = 0; row < 8; row++) {
      for (let col = 0; col < 5; col++) {
        const pieces = gameState.board[row][col].Pieces;
        for (const piece of pieces) {
          let pieceScore = 10;
          if (piece.isKing) {
            pieceScore += 1000;
            // Race distance bonus
            const dist = piece.isLight ? row : 7 - row;
            pieceScore += dist * 8;
          }

          // DOOM: Piece stacked with own Virus is effectively dead
          if (piece.type !== PieceType.Virus && pieces.length > 1) {
            const friendlyVirus = pieces.find(p =>
              p.type === PieceType.Virus && p.isLight === piece.isLight && p !== piece
            );
            if (friendlyVirus) {
              pieceScore = -(pieceScore + 5);
            }
          }

          if (piece.isLight) {
            lightTotal += pieceScore;
          } else {
            darkTotal += pieceScore;
          }
        }
      }
    }

    return lightTotal - darkTotal;
  }

  /**
   * Build breakdown for UI display
   */
  private static buildBreakdown(
    gameState: GameState,
    aiIsLight: boolean
  ): void {
    const lightPieces: EvaluationBreakdown['lightPieces'] = [];
    const darkPieces: EvaluationBreakdown['darkPieces'] = [];
    let lightTotal = 0;
    let darkTotal = 0;

    for (let row = 0; row < 8; row++) {
      for (let col = 0; col < 5; col++) {
        const pieces = gameState.board[row][col].Pieces;
        for (const piece of pieces) {
          let pieceScore = 10;
          const parts: string[] = ['piece(+10)'];

          if (piece.isKing) {
            pieceScore += 1000;
            parts.push('king(+1000)');

            const dist = piece.isLight ? row : 7 - row;
            const raceBonus = dist * 8;
            pieceScore += raceBonus;
            if (raceBonus > 0) parts.push(`race(+${raceBonus})`);
          }

          // DOOM: Piece stacked with own Virus is effectively dead
          if (piece.type !== PieceType.Virus && pieces.length > 1) {
            const friendlyVirus = pieces.find(p =>
              p.type === PieceType.Virus && p.isLight === piece.isLight && p !== piece
            );
            if (friendlyVirus) {
              const doomPenalty = -(pieceScore + 5);
              pieceScore += doomPenalty;
              parts.push(`doom(${doomPenalty})`);
            }
          }

          const entry = {
            piece: piece.type,
            position: `[${row},${col}]`,
            breakdown: parts.join(' '),
            score: pieceScore,
            isKing: piece.isKing,
          };

          if (piece.isLight) {
            lightPieces.push(entry);
            lightTotal += pieceScore;
          } else {
            darkPieces.push(entry);
            darkTotal += pieceScore;
          }
        }
      }
    }

    this.lastBreakdown = {
      lightPieces,
      darkPieces,
      lightTotal,
      darkTotal,
      finalScore: lightTotal - darkTotal,
      perspective: aiIsLight ? 'Light' : 'Dark',
    };
  }

  static getLastBreakdown(): EvaluationBreakdown | null {
    return this.lastBreakdown;
  }
}
